// Item 406 (design §6.1) — the dashboard's Suspicious block and the team-wide Seen. The block has its OWN query key,
// never criticalOpen: that one feeds the critical sound controller, and nothing may sound from this one.
import { useCallback } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { eventsApi } from '@/api/endpoints'
import { queryKeys, queryOptions } from '@/config/queryConfig'
import { CLUE_REASON_CODES, clueReasons, isOpenSuspicious, suspiciousSince } from '@/lib/suspiciousEvents'
import type { Event, PaginatedResponse } from '@/types'
import { useAcknowledgeMutation } from './useAcknowledgeCritical'
import { useSuspiciousBeta } from './useSuspiciousBeta'

/** The most rows the block can show (10 from sm, 5 on a phone); its header counts all of them with the API's count. */
export const SUSPICIOUS_PAGE_SIZE = 10

export interface OpenSuspicious {
  /** The unseen ones on the page, newest first. */
  open: Event[]
  /** How many are unseen in the window (the API's count). */
  count: number
}

const newestFirst = (a: Event, b: Event) => Date.parse(b.occurred_at) - Date.parse(a.occurred_at)

// Module level, so React Query keeps the selected value's identity while the data is unchanged. Rows seen in this tab
// (optimistic) drop out, and out of the count, before the refetch confirms it. Nothing open, nothing counted: the block
// is hidden then, also when an API without the filter answers with other events.
const selectOpen = (page: PaginatedResponse<Event>): OpenSuspicious => {
  const open = page.results.filter(isOpenSuspicious).sort(newestFirst)
  if (open.length === 0) return { open, count: 0 }
  const seenHere = page.results.length - open.length
  return { open, count: Math.max(open.length, (page.count ?? open.length) - seenHere) }
}

/** The suspicious events nobody has pressed Seen on, of the last 24 h, newest first. Superusers: every group's.
 *  #408 — beta users only: without beta, no request and no poll. */
export function useOpenSuspicious() {
  const beta = useSuspiciousBeta()
  return useQuery({
    queryKey: queryKeys.events.suspiciousOpen(),
    queryFn: () => eventsApi.getAll({
      level: 'suspicious',
      seen: 'open',
      start_time: suspiciousSince(Date.now()),
      page_size: SUSPICIOUS_PAGE_SIZE,
    }),
    select: selectOpen,
    ...queryOptions.events.suspiciousOpen,
    enabled: beta,
  })
}

/** Press Seen on one suspicious event, optimistically, in every cached events list (see useAcknowledgeMutation). */
export function useMarkSeen() {
  return useAcknowledgeMutation(
    (id: number) => eventsApi.markSeen(id),
    (id) => (e) => e.id === id,
    'seenFailed',
    undefined,
    { fields: { by: 'seen_by', at: 'seen_at' }, settleKey: queryKeys.events.suspiciousOpen() },
  )
}

/** The reasons to show for an event's clues that said yes (lib/suspiciousEvents clueReasons), with the locale behind. */
export function useClueReasons() {
  const { t, i18n } = useTranslation('common')
  const language = i18n?.language
  return useCallback(
    (event: unknown) => clueReasons(event, language, (code) =>
      CLUE_REASON_CODES.includes(code)
        ? t(`suspicious.reasons.${code as 'person' | 'spray_sound' | 'still' | 'staying' | 'at_train' | 'no_hivis' | 'backpack' | 'hoodie' | 'mask' | 'spray_can'}`)
        : null),
    [t, language],
  )
}
